import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// We have to use a class component for React error boundaries
class ErrorBoundaryComponent extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Error caught by boundary:', error, errorInfo);
    // Here you could send error to an error tracking service
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Render custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return <DefaultErrorFallback error={this.state.error} reset={this.reset} />;
    }

    return this.props.children;
  }
}

interface DefaultErrorFallbackProps {
  error: Error | null;
  reset: () => void;
}

// Default error fallback component
const DefaultErrorFallback = ({ error, reset }: DefaultErrorFallbackProps) => {
  // For translation support, using a component wrapper
  return <TranslatedErrorFallback error={error} reset={reset} />;
};

const TranslatedErrorFallback = ({ error, reset }: DefaultErrorFallbackProps) => {
  const { t } = useTranslation();
  const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center p-5 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="w-full max-w-md text-center">
        <h1 className="text-4xl font-bold mb-4">{t('errors.somethingWentWrong', 'Something Went Wrong')}</h1>
        
        <div className="bg-red-50 dark:bg-red-900/30 p-4 rounded-md mb-6 text-left overflow-auto max-h-48">
          <p className="font-mono text-sm text-red-600 dark:text-red-400">
            {error?.message || t('errors.unexpectedError', 'An unexpected error occurred')}
          </p>
        </div>
        
        <div className="flex justify-center space-x-4">
          <Button variant="outline" onClick={() => window.location.href = '/'}>
            {t('errors.goHome', 'Go Home')}
          </Button>
          <Button onClick={reset}>
            {t('errors.tryAgain', 'Try Again')}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Export as a named component
export const ErrorBoundary = ErrorBoundaryComponent; 